import {
  Form,
  Input,
  Select,
  Modal,
  Wrapper,
  useBreadcrumbRoutes,
  Button,
  useAsync,
  FormTitle,
  SubContent,
  ShowInput,
} from '@maxtropy/components';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Row, Col, Space } from 'antd';
import styles from './index.module.scss';
import { getOuListMember } from '../../../api/alarm';

import { AlarmLevel, AlarmLevelDisplay } from '@/shared/types';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getAlarmPush,
  AlarmPush,
  createAlarmPush,
  updateAlarmPush,
  PushCenterStaffLinkVoList,
  CreateAlarmPushRequest,
  UpdateAlarmPushRequest,
  OutsiderResponse,
} from '../../../api/deviceAlarmPushStrategy';

import SelectDevices from './components/SelectDevices';
import SelectAlarmRules from './components/RegulatoryRules/SelectAlarmRules';
import { useHasPermission } from '@/utils/utils';
import { PermissionsType } from '@/common/permissionsConst';
import SelectPushStaff from './components/SelectPushStaff';
import SelectPushOutSider from './components/SelectPushOutsider';

const formLayout = {
  labelCol: { span: 24 },
  wrapperCol: { span: 18 },
};

function alarmLevelOptions() {
  const level: Array<{ label: string; value: AlarmLevel }> = [
    {
      label: AlarmLevelDisplay[AlarmLevel.HIGHEST],
      value: AlarmLevel.HIGHEST,
    },
    {
      label: AlarmLevelDisplay[AlarmLevel.HIGH],
      value: AlarmLevel.HIGH,
    },
    {
      label: AlarmLevelDisplay[AlarmLevel.MIDDLE],
      value: AlarmLevel.MIDDLE,
    },
    {
      label: AlarmLevelDisplay[AlarmLevel.LOW],
      value: AlarmLevel.LOW,
    },
    {
      label: AlarmLevelDisplay[AlarmLevel.LOWEST],
      value: AlarmLevel.LOWEST,
    },
  ];
  return level.map(i => ({ label: i.label, value: i.value }));
}

export const pushMethodOptions = [
  {
    label: '基于规则推送',
    value: 'rule',
  },
  {
    label: '基于设备推送',
    value: 'device',
  },
];
export const pushConfigOptions = [
  {
    label: '基于规则配置',
    value: 'rule',
  },
  {
    label: '基于用户配置',
    value: 'customer',
  },
];

const CreateAlarmPush: FC<{ isEdit?: boolean }> = ({ isEdit = false }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [form] = Form.useForm();
  const pushMethod: string | undefined = Form.useWatch('pushMethod', form);
  const pushConfig: string | undefined = Form.useWatch('pushConfig', form);
  const pushCenterStaffLinkVoListForm: any[] | undefined = Form.useWatch('pushCenterStaffLinkVoList', form);
  const outsidersForm: any[] | undefined = Form.useWatch('outsiders', form);

  const [data, setData] = useState<AlarmPush>();

  const [pushCenterStaffLinkVoList, setPushCenterStaffLinkVoList] = useState<PushCenterStaffLinkVoList[]>([]);
  const [outsiders, setOutsiders] = useState<OutsiderResponse[]>([]);
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const [submitting, setSubmitting] = useState(false);

  const hasDevice = useHasPermission(PermissionsType.B_BASEDDEVICEPUSHBUTTON);
  const hasRule = useHasPermission(PermissionsType.B_RULE_BSAEDPUSHBUTTON);

  const pushMethodOptions = useMemo(() => {
    return [
      {
        label: '基于规则推送',
        value: 'rule',
        isShow: hasRule,
      },
      {
        label: '基于设备推送',
        value: 'device',
        isShow: hasDevice,
      },
    ].filter(i => i.isShow);
  }, [hasDevice, hasRule]);

  useEffect(() => {
    if (id) {
      getAlarmPush(id).then(res => {
        setData(res);
        setPushCenterStaffLinkVoList(res.pushCenterStaffLinkVoList ?? []);
        setOutsiders(res.outsiders ?? []);
        setOuId(res.ouId);
      });
    }
  }, [id]);

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        name: data?.name,
        pushMethod: data?.pushMethod,
        pushConfig: data?.pushConfig,
        rootMcid: data.tenantName,
        ouId: data.ouId,
        levels: data?.levels,
        pushCenterRuleLinkVoList: data?.pushCenterRuleLinkVoList ?? [],
        pushCenterStaffLinkVoList: data.pushCenterStaffLinkVoList ?? [],
        deviceAlarmPushDeviceVoList: data.deviceAlarmPushDeviceVoList ?? [],
        outsiders: data.outsiders ?? [],
      });
    }
  }, [data, form]);

  const [ouId, setOuId] = useState<number>();
  const ou = useAsync(getOuListMember, []);
  // useEffect(() => {
  //   // 重新获取ou
  //   if (ouId) {
  //     // 清空部分Form
  //     clearFormData();
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [ouId, form]);
  const clearFormData = () => {
    form.setFieldsValue({
      pushCenterStaffLinkVoList: [],
      deviceAlarmPushDeviceVoList: [],
    });
    setPushCenterStaffLinkVoList([]);
  };

  const routes = useMemo(() => {
    return [{ name: `${isEdit ? '编辑' : '新建'}设备推送` }];
  }, [isEdit]);

  const goList = () => {
    navigate(`/device/alarm/push`);
  };

  const onCacel = () => {
    Modal.confirm({
      title: <div>是否放弃所有未保存信息并返回列表？</div>,
      onOk: goList,
    });
  };

  const onFinish = (value: AlarmPush) => {
    setSubmitting(true);
    if (isEdit && id) {
      const currentDevicesId = (value.deviceAlarmPushDeviceVoList ?? []).map(i => i.deviceId);
      const devicesIds = (data?.deviceAlarmPushDeviceVoList ?? []).map(i => i.deviceId);
      const addDeviceIdList = currentDevicesId.filter(i => !devicesIds.includes(i));
      const deleteDeviceIdList = devicesIds.filter(i => !currentDevicesId.includes(i));
      const newValue: UpdateAlarmPushRequest = {
        id: data!.id,
        name: value.name,
        levels: value?.levels,
        ouId: value?.ouId,
        pushMethod: value.pushMethod,
        pushConfig: value.pushConfig,
        addDeviceIdList,
        deleteDeviceIdList,
        oldPushCenterRuleLinkRequestList: (data?.pushCenterRuleLinkVoList ?? []).map(i => ({
          ruleId: i.ruleId,
          thirdPartyIdList: i.thirdPartyAppId ?? [],
          resumeAlarmIdList: i.resumeAlarmIdList ?? [],
          deviceIdList: i.deviceIdList ?? [],
        })),
        newPushCenterRuleLinkRequestList: (value.pushCenterRuleLinkVoList ?? []).map(i => ({
          ruleId: i.ruleId,
          thirdPartyIdList: i.thirdPartyAppId ?? [],
          resumeAlarmIdList: i.resumeAlarmIdList ?? [],
          deviceIdList: i.deviceIdList,
        })),
        oldPushCenterStaffLinkRequestList: (data?.pushCenterStaffLinkVoList ?? []).map(i => ({
          staffId: i.staffId,
          thirdPartyIdList: i.thirdPartyAppId ?? [],
          resumeAlarmIdList: i.resumeAlarmIdList ?? [],
        })),
        newPushCenterStaffLinkRequestList: (value.pushCenterStaffLinkVoList ?? []).map(i => ({
          staffId: i.staffId,
          thirdPartyIdList: i.thirdPartyAppId ?? [],
          resumeAlarmIdList: i.resumeAlarmIdList ?? [],
        })),
        oldOutsidersIds: (data?.outsiders ?? []).map(i => i.id),
        newOutsiders: (value.outsiders ?? []).map(i => ({
          contactName: i.contactName,
          contactPhone: i.contactPhone,
          email: i.email,
          thirdPartyIdList: i.thirdPartyIdList ?? [],
          resumeAlarmIdList: i.resumeAlarmIdList ?? [],
        })),
      };

      updateAlarmPush(newValue)
        .then(res => {
          if (res) {
            goList();
          }
        })
        .finally(() => {
          setSubmitting(false);
        });
    } else {
      const newValue: CreateAlarmPushRequest = {
        name: value?.name,
        ouId: value?.ouId,
        levels: value?.levels,
        pushMethod: value?.pushMethod,
        pushConfig: value?.pushConfig,
        pushCenterRuleLinkRequestList: (value?.pushCenterRuleLinkVoList ?? []).map(i => ({
          ruleId: i.id,
          deviceIdList: i.deviceIdList,
          thirdPartyIdList: i.thirdPartyAppId ?? [],
        })),
        pushCenterStaffLinkRequestList: (value.pushCenterStaffLinkVoList ?? []).map(i => ({
          staffId: i.staffId,
          thirdPartyIdList: i.thirdPartyAppId ?? [],
          resumeAlarmIdList: i.resumeAlarmIdList ?? [],
        })),
        deviceIdList: (value?.deviceAlarmPushDeviceVoList ?? []).map(i => i.deviceId),
        outsidersList: (value?.outsiders ?? []).map(i => ({
          contactName: i.contactName,
          contactPhone: i.contactPhone,
          email: i.email,
          thirdPartyIdList: i.thirdPartyIdList ?? [],
          resumeAlarmIdList: i.resumeAlarmIdList ?? [],
        })),
      };

      createAlarmPush(newValue)
        .then(_ => {
          goList();
        })
        .finally(() => {
          setSubmitting(false);
        });
    }
  };

  const pushStaffValidator = useCallback(
    (value: string[]) => {
      return outsiders.map(i => i.contactPhone).some(i => value.includes(i));
    },
    [outsiders]
  );

  const outSiderValidator = useCallback(
    (value: string[]) => {
      return pushCenterStaffLinkVoList.map(i => i.phone).some(i => value.includes(i));
    },
    [pushCenterStaffLinkVoList]
  );

  const ouOptions = useMemo(() => {
    if (ou.length !== 0) {
      return ou.map(i => ({ label: i.name, value: i.id }));
    }
  }, [ou]);

  return (
    <Wrapper routes={[...(breadcrumbRoutes?.routes ?? []), ...routes]} className={styles.wrapper}>
      <FormTitle title={`${isEdit ? '编辑' : '新建'}设备推送`} />
      <Form form={form} layout="vertical" {...formLayout} onFinish={onFinish}>
        <SubContent className="mb-8">
          <Row>
            <Col span={6}>
              <Form.Item
                name="name"
                label="推送名称"
                rules={[
                  { required: true, message: '请输入推送名称' },
                  { max: 100, message: '最多输入一百个字' },
                ]}
              >
                <Input placeholder="请输入推送名称" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="pushMethod" label="推送方式" rules={[{ required: true, message: '请选择推送方式' }]}>
                <Select style={{ width: '100%' }} placeholder="请选择" options={pushMethodOptions} />
              </Form.Item>
            </Col>
            {pushMethod === 'rule' ? (
              <>
                <Col span={6}>
                  <Form.Item
                    name="pushConfig"
                    label="推送配置"
                    rules={[{ required: true, message: '请选择推送配置' }]}
                    initialValue={'rule'}
                  >
                    <Select
                      style={{ width: '100%' }}
                      placeholder="请选择"
                      options={pushConfigOptions}
                      onChange={() => form.setFieldValue('pushCenterRuleLinkVoList', undefined)}
                    />
                  </Form.Item>
                </Col>
              </>
            ) : (
              <>
                <Col span={6}>
                  <Form.Item name="ouId" label="运营单元" rules={[{ required: true, message: '请选择运营单元' }]}>
                    <Select
                      style={{ width: '100%' }}
                      placeholder="请选择"
                      onChange={(v: number) => {
                        setOuId(v);
                        clearFormData();
                      }}
                      options={ouOptions}
                      showSearch
                      optionFilterProp="label"
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name="levels"
                    label="推送报警等级"
                    rules={[{ required: true, message: '请选择推送报警等级' }]}
                  >
                    <Select
                      style={{ width: '100%' }}
                      mode="multiple"
                      placeholder="请选择"
                      options={alarmLevelOptions()}
                    />
                  </Form.Item>
                </Col>
              </>
            )}
          </Row>
        </SubContent>
        {pushMethod === 'rule' ? (
          <SubContent title="监管规则" className="mb-8">
            <Row>
              <Col span={24}>
                <Form.Item name="pushCenterRuleLinkVoList" wrapperCol={{ span: 24 }} className="formItemTable">
                  <SelectAlarmRules
                    pushConfig={pushConfig}
                    pushCenterStaffLinkVoListForm={pushCenterStaffLinkVoListForm}
                    outsidersForm={outsidersForm}
                  />
                </Form.Item>
              </Col>
            </Row>
          </SubContent>
        ) : (
          <SubContent title="监管设备" className="mb-8">
            <Row>
              <Col span={24}>
                <Form.Item name="deviceAlarmPushDeviceVoList" wrapperCol={{ span: 24 }} className="formItemTable">
                  <SelectDevices ouId={ouId} />
                </Form.Item>
              </Col>
            </Row>
          </SubContent>
        )}

        <SubContent title="内部推送人员" className="mb-8">
          <Row>
            <Col span={24}>
              <Form.Item name="pushCenterStaffLinkVoList" wrapperCol={{ span: 24 }} className="formItemTable">
                <SelectPushStaff
                  onChange={setPushCenterStaffLinkVoList}
                  ouId={ouId}
                  validator={pushStaffValidator}
                  pushMethod={pushMethod}
                  pushConfig={pushConfig}
                />
              </Form.Item>
            </Col>
          </Row>
        </SubContent>

        <SubContent title="外部推送人员" className="mb-8">
          <Row>
            <Col span={24}>
              <Form.Item name="outsiders" wrapperCol={{ span: 24 }} className="formItemTable">
                <SelectPushOutSider
                  onChange={setOutsiders}
                  validator={outSiderValidator}
                  pushMethod={pushMethod}
                  pushConfig={pushConfig}
                />
              </Form.Item>
            </Col>
          </Row>
        </SubContent>

        <Space size={8} className="sticky-footer" style={{ zIndex: 99, paddingLeft: 32 }}>
          <Button type="primary" htmlType="submit" loading={submitting}>
            保存
          </Button>
          <Button className={styles.button} onClick={onCacel}>
            取消
          </Button>
        </Space>
      </Form>
    </Wrapper>
  );
};

export default CreateAlarmPush;
